import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminOrganizations } from '@reflex/ui/client/api/admin';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SearchInput } from '@reflex/ui/components/shared/SearchInput';
import { DataTablePagination } from '@reflex/ui/components/shared/DataTablePagination';
import { CopyableId } from '@reflex/ui/components/CopyableId';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { useSearchPagination } from '@/hooks/ui/useSearchPagination';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { CreateOrganizationDialog } from '@/components/admin/CreateOrganizationDialog';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { ROUTES } from '@/lib/routes';

const DEFAULT_PAGE_SIZE = 20;

// The org shape as returned by the typed admin client. (The generated
// `Organization` type widens `emailDomains` to `unknown[]`, so derive the row
// type from the facade rather than coupling to the shared `Organization`.)
type AdminOrg = NonNullable<ReturnType<typeof useAdminOrganizations>['data']>['items'][number];

const organizationColumns: DataTableColumn<AdminOrg>[] = [
  {
    header: 'Name',
    cell: (org) => (
      <Link
        to={ROUTES.admin.organization(org.id)}
        data-testid={`admin-organization-row-${org.id}`}
        aria-label={`View organization ${org.name}`}
        className="block min-w-0 transition-opacity hover:opacity-80"
      >
        <p className="truncate font-medium">{org.name}</p>
        <p className="truncate text-xs text-muted-foreground">{org.slug}</p>
      </Link>
    ),
  },
  {
    header: 'ID',
    cell: (org) => <CopyableId id={org.id} label={`Copy organization id ${org.id}`} />,
    cellClassName: 'whitespace-nowrap',
  },
  {
    header: 'Type',
    cell: (org) =>
      org.isPersonal ? (
        <Badge variant="outline">Personal</Badge>
      ) : (
        <Badge variant="secondary">Shared</Badge>
      ),
  },
  {
    header: 'Created',
    cell: (org) => formatDateOnly(org.createdAt),
    cellClassName: 'text-xs text-muted-foreground',
  },
];

export function AdminOrganizationsPanel() {
  const { page, pageSize, q, request, setSearch, setPage, setPageSize } = useSearchPagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isLoading, isError, isFetching } = useAdminOrganizations(request);

  const organizations = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasSearch = request.q.length > 0;

  return (
    <SectionCard
      title="All Organizations"
      description={
        isLoading
          ? 'Loading…'
          : `${total.toLocaleString()} organization${total !== 1 ? 's' : ''} in the system`
      }
      icon={<Building2 />}
      contentClassName="space-y-3 pt-0"
    >
      <SearchInput
        value={q}
        onValueChange={setSearch}
        placeholder="Search by name, slug, or ID…"
        aria-label="Search organizations"
        data-testid="admin-organizations-search"
      />
      <AsyncContent
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load organizations."
      >
        {total === 0 ? (
          <EmptyState
            variant="bordered"
            icon={<Building2 className="h-8 w-8" />}
            title={hasSearch ? 'No organizations match your search' : 'No organizations yet'}
            description={
              hasSearch
                ? 'Try a different name, slug, or ID.'
                : 'Organizations will appear here as users sign up and create them.'
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <DataTable<AdminOrg>
              data={organizations}
              columns={organizationColumns}
              rowKey={(org) => org.id}
              className="rounded-none border-0"
            />
            <DataTablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              itemLabel="organization"
              isLoading={isFetching}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </AsyncContent>
    </SectionCard>
  );
}

export function AdminOrganizationsPage() {
  return (
    <AdminSubPageLayout
      title="Organizations"
      description="Every organization being managed by this instance."
      actions={<CreateOrganizationDialog />}
    >
      <AdminOrganizationsPanel />
    </AdminSubPageLayout>
  );
}
